import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, MapPin, Calendar, Clock, AlertCircle, RefreshCw, Upload } from 'lucide-react';

// Entity type display mapping
const ENTITY_TYPES = {
  'us_llc': 'US LLC',
  'us_c_corp': 'US C-Corp',
  'us_s_corp': 'US S-Corp',
  'us_sole_proprietorship': 'US Sole Prop',
};

// Service status → badge style + label
const SERVICE_BADGE = {
  active:        { cls: 'bg-[#059669] text-gray-900',       label: 'Active' },
  expiring_soon: { cls: 'bg-amber-500/20 text-amber-300',   label: 'Expiring Soon' },
  expired:       { cls: 'bg-red-500/20 text-red-300',       label: 'Expired' },
  not_added:     { cls: 'bg-[#2D333B] text-gray-400',       label: 'Not Added' },
};

const ServiceBadge = ({ prefix, status, testId }) => {
  const key = SERVICE_BADGE[status] ? status : 'not_added';
  const { cls, label } = SERVICE_BADGE[key];
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${cls}`}
      data-testid={testId}
    >
      {prefix}: {label}
    </span>
  );
};

const ApprovalBadge = ({ status, actionRequired, testId }) => {
  if (actionRequired) {
    return (
      <span
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-300"
        data-testid={testId}
      >
        <AlertCircle className="w-3 h-3" />
        Action required
      </span>
    );
  }
  if (status === 'pending_review') {
    return (
      <span
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300"
        data-testid={testId}
      >
        <Clock className="w-3 h-3" />
        Under review
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-300"
        data-testid={testId}
      >
        <AlertCircle className="w-3 h-3" />
        Rejected
      </span>
    );
  }
  return null;
};

const CompanyCard = ({ company }) => {
  const navigate = useNavigate();
  const approvalStatus = company.approval_status;
  const isSelfRegistered = company.registration_type === 'self_registered';
  // Step 1 done, no documents submitted yet — distinct state from "Under review"
  const isActionRequired =
    isSelfRegistered && approvalStatus === 'pending_review' && !company.submitted_at;
  const isPending = approvalStatus === 'pending_review' && !isActionRequired;
  const isRejected = approvalStatus === 'rejected';
  const isInReview = isPending || isRejected || isActionRequired;

  const raStatus = company.registered_agent_status || 'not_added';
  const mfStatus = company.mail_forwarding_status || 'not_added';

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const handleResubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/dashboard/companies/self-register?company_id=${company.id}`);
  };

  const handleUploadDocs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/dashboard/companies/self-register?company_id=${company.id}&step=2`);
  };

  const cardBody = (
    <>
      {/* Company Name */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#059669]" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">{company.company_name}</h3>
            <p className="text-gray-400 text-sm">
              {ENTITY_TYPES[company.entity_type] || company.entity_type}
            </p>
          </div>
        </div>
        <ApprovalBadge
          status={approvalStatus}
          actionRequired={isActionRequired}
          testId={`company-card-approval-${company.id}`}
        />
      </div>

      {/* Location & Date */}
      <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-400">
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          <span>{company.state_of_formation}</span>
        </div>
        {company.date_of_formation && (
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(company.date_of_formation)}</span>
          </div>
        )}
      </div>

      {/* Action required — Step 1 done, no documents uploaded yet */}
      {isActionRequired && (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3"
          data-testid={`company-card-action-required-block-${company.id}`}
        >
          <p className="text-sm text-red-300 mb-3">
            You haven&apos;t uploaded your formation documents yet. Your company won&apos;t be
            reviewed until you do.
          </p>
          <button
            onClick={handleUploadDocs}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#059669] text-gray-900 text-sm font-medium rounded-lg hover:opacity-90"
            data-testid={`company-card-upload-docs-btn-${company.id}`}
          >
            <Upload className="w-4 h-4" />
            Upload documents
          </button>
        </div>
      )}

      {/* Rejected — show reason + Re-submit button */}
      {isRejected && (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3"
          data-testid={`company-card-rejected-block-${company.id}`}
        >
          {company.approval_note && (
            <p className="text-sm text-red-300 mb-3">
              <span className="font-medium">Reason:</span> {company.approval_note}
            </p>
          )}
          <button
            onClick={handleResubmit}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#059669] text-gray-900 text-sm font-medium rounded-lg hover:opacity-90"
            data-testid={`company-card-resubmit-btn-${company.id}`}
          >
            <RefreshCw className="w-4 h-4" />
            Re-submit
          </button>
        </div>
      )}

      {/* Pending review — explanatory copy */}
      {isPending && (
        <div
          className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3 text-sm text-amber-300"
          data-testid={`company-card-pending-block-${company.id}`}
        >
          Our team is reviewing your formation documents. We&apos;ll email you when it&apos;s approved.
        </div>
      )}

      {/* Service Badges (only when company is active/approved) */}
      {!isInReview && (
        <div className="flex flex-wrap gap-2">
          <ServiceBadge prefix="RA" status={raStatus} testId={`company-card-ra-${company.id}`} />
          <ServiceBadge prefix="MF" status={mfStatus} testId={`company-card-mf-${company.id}`} />
        </div>
      )}
    </>
  );

  // Action-required and rejected cards link to the resubmit page; everything else
  // links to the company detail page.
  const target = isActionRequired
    ? `/dashboard/companies/self-register?company_id=${company.id}&step=2`
    : isRejected
    ? `/dashboard/companies/self-register?company_id=${company.id}`
    : `/dashboard/companies/${company.id}`;

  return (
    <Link
      to={target}
      className="block bg-[#111827] border border-gray-800 rounded-lg p-5 hover:border-[#059669] transition-all duration-200 hover:shadow-lg"
      data-testid={`company-card-${company.id}`}
    >
      {cardBody}
    </Link>
  );
};

export default CompanyCard;
